import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { ColDef } from 'ag-grid-community/dist/lib/entities/colDef';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  AddressAddresseeReferenceService,
  AddressComponent,
  AddressCreateAddresseesModalComponent,
  AddressDetails,
  AddressService,
  AggFunc,
  BaseComponent,
  BaseEditFormComponent,
  CustomerAddress,
  DeliveryTermReferenceService,
  FormService,
  GridService,
  Guid,
  IdDescription,
  ModalService,
  OkModalComponent,
  PrintService,
  SalesOrderHeaderDetails,
  ShipperSupplier,
  Supplier,
  SupplierService,
  TabGridComponent,
  TmsFormlyService,
  YesNoModalComponent,
} from 'tms-lib';
import {
  SalesDespatchNoteService,
  ShipperInterfaceDetails,
  ShipperInterfaceDetailsService,
  ShipperInterfaceHeader,
  ShipperInterfaceHeaderService,
  ShipperResponse,
  ShippingService,
  ShippingServiceService,
} from 'tms-web-shared';
import { ShipperInterfaceDetailsModalComponent } from '../shipper-interface-details-modal/shipper-interface-details-modal.component';

@Component({
  selector: 'app-despatch-shipper-interface',
  templateUrl: './despatch-shipper-interface.component.html',
  styleUrl: './despatch-shipper-interface.component.scss',
})
export class DespatchShipperInterfaceComponent extends BaseComponent implements OnInit, OnDestroy {
  @ViewChild(BaseEditFormComponent) form: BaseEditFormComponent;
  @ViewChild(TabGridComponent) grid: TabGridComponent;

  @Input() salesDespatchNoteId: string;
  @Input() goodsDeliveryNoteId: string;
  @Input() shipperInterfaceHeaderId: string;
  @Output() supplierChanged = new EventEmitter<Supplier>();

  fields: FormlyFieldConfig[];
  buttonFields: FormlyFieldConfig[];
  show = true;

  private shippingServices$ = new BehaviorSubject<ShippingService[]>([]);
  private deliveryAddressees$ = new BehaviorSubject<IdDescription[]>([]);
  private deliveryTerms$: Observable<IdDescription[]>;
  private modalLevel: number;
  private saveSubscription: Subscription;
  private shipperSupplierId: string;

  columnDefs: ColDef[] = [
    this.gridService.text('shippingPackKey', $localize`Shipping Pack`, { aggFunc: AggFunc.count }),
    this.gridService.text('shippingPackDescription', $localize`Description`),
    this.gridService.numberFormat('grossWeight', $localize`Gross Weight`, {
      aggFunc: AggFunc.sum,
    }),
    this.gridService.numberFormat('length', $localize`Length`),
    this.gridService.numberFormat('width', $localize`Width`),
    this.gridService.numberFormat('height', $localize`Height`),
  ];

  constructor(
    public shipperInterfaceHeaderService: ShipperInterfaceHeaderService,
    private shipperInterfaceDetailsService: ShipperInterfaceDetailsService,
    private salesDespatchNoteService: SalesDespatchNoteService,
    private supplierService: SupplierService,
    private shippingServiceService: ShippingServiceService,
    private formService: FormService,
    private formlyService: TmsFormlyService,
    private gridService: GridService,
    private modalService: ModalService,
    private printService: PrintService,
    private addressService: AddressService,
    private addressAddresseeReferenceService: AddressAddresseeReferenceService,
    private deliveryTermService: DeliveryTermReferenceService,
  ) {
    super();
  }
  ngOnInit(): void {
    this.deliveryTerms$ = this.deliveryTermService.get();
    this.getFields();

    this.modalLevel = this.modalService.modalLevel;
    this.salesDespatchNoteService.refreshDetails.pipe(takeUntil(this.destroy$)).subscribe(() => this.form.getModel());
    this.shipperInterfaceHeaderService.refreshDetails
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.form.getModel());
  }

  ngOnDestroy() {
    super.ngOnDestroy();

    if (this.saveSubscription) {
      this.saveSubscription.unsubscribe();
    }
  }

  get model(): ShipperInterfaceHeader {
    return this.form?.model;
  }

  modelLoaded(model: ShipperInterfaceHeader) {
    this.show = !!model.interfaceNumber;
    this.shipperSupplierId = model.supplierId;
    this.getShippingServices(this.shipperSupplierId);
    this.getDeliveryAddressees(model.deliveryAddressId);
  }

  saved() {
    this.shipperInterfaceHeaderService.refreshDetails.next();
  }

  private getFields() {
    this.fields = [
      ...this.formlyService.threeColumnGrid(
        [
          this.formlyService.textReadonly('interfaceNumber', $localize`Interface Number`),
          this.formlyService.date('dateShipped', $localize`Date Shipped`),
          ...this.formlyService.supplierShipperSearch(
            'supplierSearch',
            'supplierId',
            undefined,
            (model: Supplier) => this.changeSupplier(model),
            () => this.clearSupplier(),
          ),
          this.formlyService.textReadonly('supplierKey', $localize`Shipper`, undefined, { required: true }),
          this.formlyService.textReadonly('supplierName', $localize`Name`, undefined, { required: true }),
          this.formlyService.textReadonly('shipperAccountKey', $localize`Shipper Account`, undefined, {
            required: true,
          }),
          this.formlyService.select(
            'shippingServiceId',
            $localize`Shipping Service`,
            this.shippingServices$,
            undefined,
            {
              change: (field: FormlyFieldConfig, event) => this.changeShippingService(event?.value),
              required: true,
            },
          ),
          this.formlyService.textReadonly('shippingServiceKey', $localize`Shipping Key`, undefined, { required: true }),
          this.formlyService.numberReadonly('shippingCharge', $localize`Shipping Charge`),
        ],
        [
          ...this.formlyService.customerAddressSearch(
            'deliveryAddressSearch',
            'deliveryAddressMultiLine',
            $localize`Delivery Address`,
            (model: SalesOrderHeaderDetails) => model.customerId,
            (model: CustomerAddress) => this.changeDeliveryAddress(model),
            () => this.clearDeliveryAddress(),
            {
              editButton: true,
              onClick: () => this.editAddress(this.model.deliveryAddressId),
            },
          ),
          this.formlyService.select(
            'deliveryAddresseeId',
            $localize`Delivery Addressee`,
            this.deliveryAddressees$,
            undefined,
            { create: (event: any) => this.addAddressee(this.model.deliveryAddressId, event) },
          ),
          this.formlyService.text('deliveryEmail', $localize`Delivery Email`),
          this.formlyService.text('deliveryTel', $localize`Delivery Telephone`),
          this.formlyService.select('deliveryTermId', $localize`Delivery Term`, this.deliveryTerms$),
        ],
        [
          this.formlyService.textReadonly('sellingCompanyName', $localize`Selling Company`),
          this.formlyService.textReadonly('customerRef1', $localize`Order Reference`),
          this.formlyService.textReadonly('customerRef2', $localize`Line Reference`),
          this.formlyService.textReadonly('productType', $localize`Product`),
          this.formlyService.textReadonly('trackingNumber', $localize`Tracking Number`),
          this.formlyService.textarea('shippingNotes', $localize`Shipping Notes`),
        ],
      ),
    ];

    this.buttonFields = [
      this.formlyService.button('Book Shipping', () => this.bookShipping()),
      this.formlyService.button('Reprint Label', () => this.reprintLabel()),
    ];
  }

  private changeSupplier(model: Supplier) {
    this.supplierChanged.emit(model);
    this.supplierService
      .getSupplierForShipping(model.id, this.model.sellingCompanyId, this.model.shipperAccountWarehouseId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((shipperSupplier: ShipperSupplier) => {
        this.form.updateModel(shipperSupplier);
        this.getShippingServices(shipperSupplier.supplierId);
      });
  }

  private clearSupplier() {
    this.form.updateModel({
      supplierId: undefined,
      supplierKey: undefined,
      supplierName: undefined,
      shipperAccountKey: undefined,
      shippingServiceId: undefined,
    });
    this.changeShippingService(undefined);
  }

  private getShippingServices(supplierId: string) {
    this.shippingServiceService
      .getForSupplier(supplierId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((shippingServices: ShippingService[]) => {
        this.shippingServices$.next(shippingServices);
        if (
          shippingServices.every(
            (shippingService: ShippingService) => shippingService.id !== this.model.shippingServiceId,
          )
        ) {
          this.form.updateModel({ shippingServiceId: undefined }, false);
          this.changeShippingService(undefined);
        }
      });
  }

  private changeShippingService(shippingServiceId: string) {
    const shippingService = this.shippingServices$.value.find((x) => x.id == shippingServiceId);
    this.form.updateModel({
      shippingServiceKey: shippingService?.key,
      shippingCharge: shippingService?.shippingCost,
    });
  }

  private bookShipping() {
    if (this.formService.anyFormChanges(this.modalLevel)) {
      const { modal, component } = this.modalService.openSmall<YesNoModalComponent>(YesNoModalComponent);
      component.header = $localize`Save Changes`;
      component.message = $localize`OK to save changes before booking shipping for this delivery?`;
      modal.result.then(
        (yes: boolean) => {
          if (yes) {
            this.saveSubscription = this.formService.saveComplete.pipe(takeUntil(this.destroy$)).subscribe(() => {
              this.saveSubscription.unsubscribe();
              this.confirmBookShipping();
            });
            this.formService.save.next(this.modalLevel);
          }
        },
        () => {},
      );
    } else {
      this.confirmBookShipping();
    }
  }

  private confirmBookShipping() {
    const { modal, component } = this.modalService.openSmall<YesNoModalComponent>(YesNoModalComponent);
    component.header = $localize`Book Shipping`;
    component.message = $localize`OK to book shipping for this delivery?`;
    modal.result.then(
      (yes: boolean) => {
        if (yes) {
          this.shipperInterfaceHeaderService
            .bookShipping(this.model.id, this.model.shippingNotes)
            .pipe(takeUntil(this.destroy$))
            .subscribe((response: ShipperResponse) => {
              if (response.trackingNumber) {
                this.form.updateModel({ trackingNumber: response.trackingNumber }, false);
                setTimeout(() => this.reprintLabel(), 0);
                this.shipperInterfaceHeaderService.refreshDetails.next();
              } else {
                const { modal, component } = this.modalService.openSmall<OkModalComponent>(OkModalComponent);
                component.header = $localize`Book Shipping`;
                component.message = $localize`Booking Failed` + ` - ${response.errorMessage}`;
                modal.result.then(
                  () => {},
                  () => {},
                );
              }
            });
        }
      },
      () => {},
    );
  }

  private reprintLabel() {
    this.shipperInterfaceHeaderService
      .reprintLabel(
        this.salesDespatchNoteId === Guid.empty ? this.goodsDeliveryNoteId : this.salesDespatchNoteId,
        this.model.id,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe((response: ShipperResponse) => {
        if (response.trackingNumber) {
          this.form.updateModel({ trackingNumber: response.trackingNumber }, false);
          this.printService.printShippingLabel(response);
          this.shipperInterfaceHeaderService.refreshDetails.next();
        } else {
          const { modal, component } = this.modalService.openSmall<OkModalComponent>(OkModalComponent);
          component.header = $localize`Book Shipping`;
          component.message = $localize`Booking Failed` + ` - ${response.errorMessage}`;
          modal.result.then(
            () => {},
            () => {},
          );
        }
      });
  }

  private changeDeliveryAddress(model: CustomerAddress) {
    this.addressService
      .edit(model.addressId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((address: AddressDetails) => this.updateDeliveryAddress(address));
  }

  private updateDeliveryAddressee(addressId: string, addresseeId: string) {
    this.form.updateModel({ deliveryAddresseeId: addresseeId });
    this.getDeliveryAddressees(addressId);
  }

  private clearDeliveryAddress() {
    this.form.updateModel({
      deliveryAddressId: undefined,
      deliveryAddressMultiLine: undefined,
      deliveryAddresseeId: undefined,
      deliveryEmail: undefined,
      deliveryTel: undefined,
    });
  }

  private getDeliveryAddressees(addressId: string) {
    this.addressAddresseeReferenceService
      .getForAddress(addressId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((deliveryAddressees: IdDescription[]) => {
        this.deliveryAddressees$.next(deliveryAddressees);
        if (
          !!this.model.deliveryAddresseeId &&
          deliveryAddressees.every(
            (deliveryAddressee: IdDescription) => deliveryAddressee.id !== this.model.deliveryAddresseeId,
          )
        ) {
          this.form.updateModel({ deliveryAddresseeId: undefined });
        }
      });
  }

  private editAddress(addressId: string) {
    if (!addressId) return;

    this.addressService
      .edit(addressId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((address: AddressDetails) => {
        const { modal, component } = this.modalService.openLarge<AddressComponent>(AddressComponent);
        component.id = address.id;
        component.isModal = true;
        modal.result.then(
          () => {
            this.addressService
              .edit(address.id)
              .pipe(takeUntil(this.destroy$))
              .subscribe((model: AddressDetails) => this.updateDeliveryAddress(model));
          },
          () => {
            this.addressService
              .edit(address.id)
              .pipe(takeUntil(this.destroy$))
              .subscribe((model: AddressDetails) => this.updateDeliveryAddress(model));
          },
        );
      });
  }

  private updateDeliveryAddress(address: AddressDetails) {
    this.form.updateModel({
      deliveryAddressId: address.id,
      deliveryAddressMultiLine: address.multiLine,
      deliveryEmail: address.email,
      deliveryTel: address.phone,
    });
    this.getDeliveryAddressees(address.id);
  }

  private addAddressee(addressId: string, event: any) {
    if (!addressId || !event.target.nodeName) return;
    const { modal, component } = this.modalService.openMedium<AddressCreateAddresseesModalComponent>(
      AddressCreateAddresseesModalComponent,
    );
    component.addressId = addressId;
    component.id = undefined;
    modal.result.then(
      () => this.updateDeliveryAddressee(addressId, component.newAddressee.id),
      () => {},
    );
  }

  create() {
    this.openShipperInterfaceDetails(undefined);
  }

  editDetails(model: ShipperInterfaceDetails) {
    this.openShipperInterfaceDetails(model);
  }

  delete(model: ShipperInterfaceDetails) {
    this.shipperInterfaceDetailsService
      .delete(model.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.form.getModel());
  }

  private openShipperInterfaceDetails(model: ShipperInterfaceDetails) {
    const { modal, component } = this.modalService.openMedium<ShipperInterfaceDetailsModalComponent>(
      ShipperInterfaceDetailsModalComponent,
    );
    component.id = model?.id;
    component.shippingInterfaceHeaderId = this.shipperInterfaceHeaderId;
    component.shipperSupplierId = this.shipperSupplierId;
    modal.result.then(
      () =>
        this.shipperInterfaceDetailsService
          .search('', this.model.id)
          .pipe(takeUntil(this.destroy$))
          .subscribe((details) => {
            this.form.updateModel(
              {
                shipperInterfaceDetails: details,
              },
              false,
            );
          }),
      () => {},
    );
  }
}
